/**
 * Parcours vidéo par gamme : film court (plans vidéo, voix off, sous-titres calés sur la voix)
 * ouvert dans une fenêtre plein écran par les boutons [data-cg-parcours-open="<gamme>"].
 * Les plans viennent du JSON rendu par la section cg-parcours.
 */
const ICON_PAUSE = '<svg viewBox="0 0 12 12" fill="currentColor" aria-hidden="true"><rect x="2" y="1.5" width="2.6" height="9" rx=".6"/><rect x="7.4" y="1.5" width="2.6" height="9" rx=".6"/></svg>';
const ICON_PLAY = '<svg viewBox="0 0 12 12" fill="currentColor" aria-hidden="true"><path d="M2.5 1.2l8 4.8-8 4.8z"/></svg>';
const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

/** @param {string} value */
const esc = (value) =>
  String(value).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c] ?? c);

/* ---------- Schémas animés (plans d'explication) ----------
   Chaque schéma est un SVG fin, au trait, calé sur la ligne de temps du plan :
   data-d « début durée » trace le trait, data-f fait apparaître, data-c compte un nombre,
   data-m déplace un élément le long d'un chemin, data-g agrandit une onde qui s'efface.
   Deux mises en page : large (ordinateur) et verticale (téléphone, textes plus grands). */
const INK = '#16271f';
const MUTED = '#5d6459';
const HAIR = 'rgb(22 39 31 / 0.28)';
const SOFT = 'rgb(22 39 31 / 0.1)';
const BG = '#d6d4cf';

/** @param {number} x */
const ease = (x) => (x <= 0 ? 0 : x >= 1 ? 1 : 1 - Math.pow(1 - x, 3));
/** @param {number} a @param {number} b */
const tl = (a, b) => `${a.toFixed(2)} ${b.toFixed(2)}`;
/** @param {number} value @param {number} dec */
const fr = (value, dec) => value.toFixed(dec).replace('.', ',');
/** Petite étiquette en capitales espacées */
const label = (/** @type {number} */ x, /** @type {number} */ y, /** @type {string} */ text, anchor = 'start', t = '0 0.6', fill = MUTED, size = 11.5) =>
  `<text x="${x}" y="${y}" text-anchor="${anchor}" font-size="${size}" font-weight="600" letter-spacing="${(size * 0.12).toFixed(2)}" fill="${fill}" stroke="none" data-f="${t}">${esc(text)}</text>`;
/** Repère : point + trait vers une étiquette */
const callout = (/** @type {number} */ x1, /** @type {number} */ y1, /** @type {number} */ x2, /** @type {number} */ y2, /** @type {string} */ text, /** @type {number} */ start, anchor = 'start', size = 11.5) =>
  `<circle cx="${x1}" cy="${y1}" r="3.5" fill="${INK}" stroke="none" data-f="${tl(start, 0.3)}"/>
   <path d="M${x1} ${y1}L${x2} ${y2}" stroke="${INK}" stroke-width="1" data-d="${tl(start + 0.1, 0.5)}"/>
   ${label(anchor === 'end' ? x2 - 8 : x2 + 8, y2 + size * 0.35, text, anchor, tl(start + 0.45, 0.4), INK, size)}`;
/** Pièce de chewing-gum vue de 3/4, au trait */
const gum = (/** @type {number} */ cx, /** @type {number} */ cy, /** @type {number} */ w, /** @type {number} */ start) => {
  const h = w * 0.62;
  const x = cx - w / 2;
  const y = cy - h / 2;
  const r = h * 0.42;
  const dots = Array.from({ length: 14 }, (_, i) => {
    const px = x + w * (0.18 + ((i * 37) % 64) / 100);
    const py = y + h * (0.22 + ((i * 53) % 58) / 100);
    return `<circle cx="${px.toFixed(1)}" cy="${py.toFixed(1)}" r="1.3" fill="${INK}" fill-opacity=".35" stroke="none" data-f="${tl(start + 0.8 + i * 0.04, 0.3)}"/>`;
  }).join('');
  return `<rect x="${x}" y="${y}" width="${w}" height="${h}" rx="${r}" stroke="${INK}" stroke-width="1.4" data-d="${tl(start, 1)}"/>
    <path d="M${x + r * 0.7} ${y + h * 0.28}C${x + w * 0.3} ${y + h * 0.16} ${x + w * 0.62} ${y + h * 0.14} ${x + w * 0.8} ${y + h * 0.22}" stroke="${INK}" stroke-opacity=".45" stroke-width="1" data-d="${tl(start + 0.6, 0.6)}"/>
    ${dots}`;
};
/** Grand nombre qui compte, avec son unité */
const bigNumber = (/** @type {number} */ x, /** @type {number} */ y, /** @type {number} */ to, /** @type {string} */ unit, /** @type {number} */ start, /** @type {number} */ size, anchor = 'start') =>
  `<text x="${x}" y="${y}" text-anchor="${anchor}" font-size="${size}" letter-spacing="${(-size * 0.03).toFixed(1)}" fill="${INK}" stroke="none" data-f="${tl(start - 0.2, 0.3)}"><tspan data-c="${tl(start, 1.4)} 0 ${to} 0">0</tspan><tspan font-size="${(size * 0.42).toFixed(0)}" letter-spacing="0" dx="${(size * 0.1).toFixed(0)}">${unit}</tspan></text>`;

/** @type {Record<string, (s: any, acc: string, m: boolean) => string>} */
const ART = {
  /* Courbe de Stephan : le pH de la plaque chute après le repas puis remonte grâce à la salive */
  curve(s, acc, m) {
    const c1 = s.cap?.[1]?.[0] ?? 3;
    const c2 = s.cap?.[2]?.[0] ?? 6;
    const g = m ? { x0: 54, k: 5.4, top: 92, bot: 330, w: 400, h: 470, fs: 14, tick: 20 } : { x0: 110, k: 10, top: 100, bot: 380, w: 800, h: 440, fs: 11.5, tick: 10 };
    const y = (/** @type {number} */ ph) => g.top + (7.2 - ph) * ((g.bot - g.top) / 2.2);
    const x = (/** @type {number} */ min) => g.x0 + min * g.k;
    const tail = `C${x(16)} ${y(5.2)} ${x(21)} ${y(5.62)} ${x(25)} ${y(5.9)}C${x(31)} ${y(6.3)} ${x(36)} ${y(6.48)} ${x(40)} ${y(6.6)}C${x(47)} ${y(6.79)} ${x(53)} ${y(6.98)} ${x(60)} ${y(7)}`;
    const curve = `M${g.x0} ${y(7)}L${x(5)} ${y(7)}C${x(7.5)} ${y(7)} ${x(9)} ${y(5.2)} ${x(12)} ${y(5.2)}${tail}`;
    const recovery = `M${x(12)} ${y(5.2)}${tail}`;
    const ticksY = [7, 6.5, 6, 5.5]
      .map((ph) => `<path d="M${g.x0 - 6} ${y(ph)}H${g.x0}" stroke="${INK}" stroke-width="1" data-f="0.3 0.4"/><text x="${g.x0 - 12}" y="${y(ph) + 4}" text-anchor="end" font-size="${g.fs + 0.5}" fill="${MUTED}" stroke="none" data-f="0.4 0.4">${fr(ph, 1)}</text>`)
      .join('');
    const ticksX = [0, 10, 20, 30, 40, 50, 60]
      .filter((v) => v % g.tick === 0)
      .map((v) => `<path d="M${x(v)} ${g.bot}V${g.bot + 6}" stroke="${INK}" stroke-width="1" data-f="0.5 0.4"/><text x="${x(v)}" y="${g.bot + 24}" text-anchor="middle" font-size="${g.fs + 0.5}" fill="${MUTED}" stroke="none" data-f="0.6 0.4">${v}</text>`)
      .join('');
    const right = x(60);
    return `<svg viewBox="0 0 ${g.w} ${g.h}" fill="none" aria-hidden="true">
      <defs>
        <pattern id="cg-hatch" width="7" height="7" patternUnits="userSpaceOnUse" patternTransform="rotate(45)"><path d="M0 0V7" stroke="${INK}" stroke-opacity=".5" stroke-width="1.2"/></pattern>
        <clipPath id="cg-over"><path d="${curve}L${right} 0L${g.x0} 0Z"/></clipPath>
      </defs>
      <path d="M${g.x0} ${g.top - 30}V${g.bot}H${right + 16}" stroke="${INK}" stroke-width="1.2" data-d="0 0.9"/>
      ${ticksY}${ticksX}
      ${label(g.x0, g.top - 42, 'PH DE LA PLAQUE', 'start', '0.3 0.5', MUTED, g.fs)}
      ${label(right + 16, g.bot + 50, 'MINUTES', 'end', '0.6 0.5', MUTED, g.fs)}
      <rect x="${g.x0}" y="${y(5.5)}" width="${right - g.x0}" height="${g.bot - y(5.5)}" fill="url(#cg-hatch)" clip-path="url(#cg-over)" stroke="none" data-f="${tl(c1, 0.8)}"/>
      <path d="M${g.x0} ${y(5.5)}H${right}" stroke="${INK}" stroke-opacity=".55" stroke-dasharray="5 6" stroke-width="1" data-f="0.8 0.5"/>
      ${label(right, y(5.5) - 10, 'SEUIL CRITIQUE · PH 5,5', 'end', '0.9 0.5', INK, g.fs)}
      <path d="M${x(5)} ${g.top - 18}V${g.bot}" stroke="${INK}" stroke-opacity=".35" stroke-dasharray="2 5" stroke-width="1" data-f="1 0.4"/>
      ${label(x(5) + 8, g.top - 8, 'REPAS', 'start', '1 0.4', INK, g.fs)}
      <path id="cg-ph" d="${curve}" stroke="${INK}" stroke-width="2" stroke-linecap="round" data-d="${tl(1.1, Math.max(2, c1 - 0.6))}"/>
      <path d="${recovery}" stroke="${acc}" stroke-width="3" stroke-linecap="round" data-d="${tl(c2, 2.4)}"/>
      <g data-m="cg-ph ${tl(1.1, c2 + 2.4 - 1.1)}"><circle r="9" fill="${acc}" fill-opacity=".25" stroke="none"/><circle r="4.5" fill="${INK}" stroke="none"/></g>
      ${callout(x(8.6), y(6.1), x(8.6) + (m ? 44 : 70), y(6.1) + (m ? 4 : -40), 'ACIDES', 1.8, 'start', g.fs)}
      ${callout(x(11), y(5.36), x(11) + (m ? 30 : 110), g.bot + (m ? 74 : 36), 'DÉMINÉRALISATION', c1 + 0.3, 'start', g.fs)}
      ${callout(x(33), y(6.42), x(33) + (m ? 14 : 40), y(6.42) - (m ? 58 : 62), 'SALIVE', c2 + 0.6, 'start', g.fs)}
    </svg>`;
  },

  /* Cadran gradué : 20 minutes après le repas */
  clock(s, acc, m) {
    const c1 = s.cap?.[1]?.[0] ?? 2;
    const f = m ? 1.4 : 1;
    const cx = 400;
    const cy = 225;
    const r = 150;
    let ticks = '';
    for (let i = 0; i < 60; i++) {
      const a = (i / 60) * Math.PI * 2 - Math.PI / 2;
      const major = i % 5 === 0;
      const r1 = r + 12;
      const r2 = r1 + (major ? 14 : 6);
      ticks += `M${(cx + Math.cos(a) * r1).toFixed(1)} ${(cy + Math.sin(a) * r1).toFixed(1)}L${(cx + Math.cos(a) * r2).toFixed(1)} ${(cy + Math.sin(a) * r2).toFixed(1)}`;
    }
    const nums = Array.from({ length: 12 }, (_, i) => {
      const a = ((i * 5) / 60) * Math.PI * 2 - Math.PI / 2;
      return `<text x="${(cx + Math.cos(a) * (r + 46)).toFixed(1)}" y="${(cy + Math.sin(a) * (r + 46) + 5).toFixed(1)}" text-anchor="middle" font-size="${12 * f}" fill="${i <= 4 ? INK : MUTED}" stroke="none" data-f="${tl(0.6 + i * 0.05, 0.4)}">${i * 5}</text>`;
    }).join('');
    const end = (20 / 60) * Math.PI * 2 - Math.PI / 2;
    const arc = `M${cx} ${cy - r}A${r} ${r} 0 0 1 ${(cx + Math.cos(end) * r).toFixed(1)} ${(cy + Math.sin(end) * r).toFixed(1)}`;
    return `<svg viewBox="${m ? '180 0 440 450' : '0 0 800 470'}" fill="none" aria-hidden="true">
      <circle cx="${cx}" cy="${cy}" r="${r}" stroke="${HAIR}" stroke-width="1" data-d="0 1.1"/>
      <path d="${ticks}" stroke="${INK}" stroke-width="1.1" data-f="0.3 0.8"/>
      ${nums}
      <path id="cg-arc" d="${arc}" stroke="${acc}" stroke-width="6" stroke-linecap="round" data-d="${tl(c1, 3)}"/>
      <path d="M${cx} ${cy}V${cy - r + 18}" stroke="${INK}" stroke-opacity=".35" stroke-width="1" data-f="0.9 0.4"/>
      <g data-m="cg-arc ${tl(c1, 3)}"><circle r="11" fill="${acc}" fill-opacity=".3" stroke="none"/><circle r="5" fill="${INK}" stroke="none"/></g>
      <text x="${cx}" y="${cy + 14}" text-anchor="middle" font-size="60" letter-spacing="-2" fill="${INK}" stroke="none" data-f="${tl(c1 - 0.2, 0.4)}"><tspan data-c="${tl(c1, 3)} 0 20 0">0</tspan><tspan font-size="24" letter-spacing="0" dx="6">min</tspan></text>
      ${label(cx, cy + 46, 'APRÈS AVOIR MANGÉ OU BU', 'middle', tl(c1 + 0.6, 0.5), MUTED, m ? 13 : 11.5)}
    </svg>`;
  },

  /* Étiquette nutritionnelle : seule la ligne « sucres » est lisible, à 0 g */
  sugar(s, acc, m) {
    const c1 = s.cap?.[1]?.[0] ?? 2;
    const L = m ? { x: 40, y: 230, w: 320, gx: 200, gy: 110, gw: 200, vw: 400, vh: 520 } : { x: 400, y: 120, w: 320, gx: 220, gy: 250, gw: 220, vw: 800, vh: 440 };
    const fs = m ? 13 : 11.5;
    const rows = [0, 1, 2, 3, 4, 5]
      .map((i) => {
        const yy = L.y + 76 + i * 36;
        const key = i === 2;
        const w = [118, 92, 0, 104, 76, 130][i];
        const indent = i === 1 || i === 2 || i === 3 ? 18 : 0;
        return `<path d="M${L.x + 20} ${yy + 14}H${L.x + L.w - 20}" stroke="${INK}" stroke-opacity="${key ? 0.5 : 0.18}" stroke-width="1" data-d="${tl(0.8 + i * 0.12, 0.5)}"/>
          ${key
            ? `<text x="${L.x + 20 + indent}" y="${yy + 5}" font-size="${m ? 18 : 16}" fill="${INK}" stroke="none" data-f="${tl(c1 - 0.6, 0.4)}">dont sucres</text>
               <text x="${L.x + L.w - 20}" y="${yy + 6}" text-anchor="end" font-size="${m ? 24 : 22}" fill="${INK}" stroke="none" data-f="${tl(c1 - 0.4, 0.4)}">0 g</text>
               <path d="M${L.x + L.w - 70} ${yy + 14}H${L.x + L.w - 20}" stroke="${acc}" stroke-width="3" data-d="${tl(c1, 0.6)}"/>`
            : `<rect x="${L.x + 20 + indent}" y="${yy - 5}" width="${w}" height="9" rx="4.5" fill="${SOFT}" stroke="none" data-f="${tl(0.9 + i * 0.12, 0.4)}"/>
               <rect x="${L.x + L.w - 44 - (i % 3) * 8}" y="${yy - 5}" width="${24 + (i % 3) * 8}" height="9" rx="4.5" fill="${SOFT}" stroke="none" data-f="${tl(0.9 + i * 0.12, 0.4)}"/>`}`;
      })
      .join('');
    return `<svg viewBox="0 0 ${L.vw} ${L.vh}" fill="none" aria-hidden="true">
      ${gum(L.gx, L.gy, L.gw, 0.2)}
      ${label(L.gx, L.gy + L.gw * 0.31 + 32, 'UNE PIÈCE', 'middle', '1.2 0.5', MUTED, fs)}
      <rect x="${L.x}" y="${L.y}" width="${L.w}" height="300" rx="14" stroke="${INK}" stroke-width="1.2" data-d="0.4 1"/>
      ${label(L.x + 20, L.y + 32, 'VALEURS NUTRITIONNELLES', 'start', '0.7 0.4', INK, fs)}
      <path d="M${L.x + 20} ${L.y + 48}H${L.x + L.w - 20}" stroke="${INK}" stroke-width="2.5" data-d="0.7 0.5"/>
      ${rows}
    </svg>`;
  },

  /* Goutte de salive : elle tombe, les ondes s'étalent */
  drop(s, acc, m) {
    const c1 = s.cap?.[1]?.[0] ?? 2.4;
    const fall = (/** @type {number} */ t0) => `<g data-m="cg-fall ${tl(t0, 0.9)}"><path d="M0 -46C14 -22 26 -8 26 8C26 24 14 34 0 34C-14 34 -26 24 -26 8C-26 -8 -14 -22 0 -46Z" fill="${acc}" fill-opacity=".18" stroke="${INK}" stroke-width="1.3"/><path d="M-12 6C-12 -2 -8 -10 -3 -16" stroke="${INK}" stroke-opacity=".5" stroke-width="1"/></g>`;
    const waves = (/** @type {number} */ t0) =>
      [0, 0.35, 0.7].map((d) => `<ellipse cx="400" cy="330" rx="20" ry="5" stroke="${INK}" stroke-width="1.1" data-g="${tl(t0 + d, 1.8)} 20 200 5 34"/>`).join('');
    return `<svg viewBox="${m ? '170 40 460 400' : '0 0 800 440'}" fill="none" aria-hidden="true">
      <path id="cg-fall" d="M400 60V296" stroke="none"/>
      <path d="M180 330H620" stroke="${INK}" stroke-opacity=".45" stroke-width="1" data-d="0.2 1"/>
      ${fall(0.5)}${waves(1.35)}
      ${fall(c1)}${waves(c1 + 0.85)}
      ${callout(436, 300, m ? 520 : 560, 236, 'SALIVE', 1.4, 'start', m ? 15 : 11.5)}
    </svg>`;
  },

  /* Formule développée de la caféine (1,3,7-triméthylxanthine) */
  caffeine(s, acc, m) {
    const c1 = s.cap?.[1]?.[0] ?? 2.2;
    const dx = m ? -144 : 0;
    /** @type {Record<string, number[]>} */
    const P = {
      C6: [300, 160], C5: [360.6, 195], C4: [360.6, 265], N3: [300, 300], C2: [239.4, 265], N1: [239.4, 195],
      N7: [427.2, 173.4], C8: [468.3, 230], N9: [427.2, 286.6],
      O6: [300, 104], O2: [190, 293], M1: [190, 167], M3: [300, 356], M7: [450, 117],
    };
    Object.values(P).forEach((p) => (p[0] += dx));
    const b = (/** @type {string} */ a, /** @type {string} */ z, /** @type {number} */ t, double = false) => {
      const [x1, y1] = P[a];
      const [x2, y2] = P[z];
      let d = `M${x1} ${y1}L${x2} ${y2}`;
      if (double) {
        const ddx = x2 - x1;
        const ddy = y2 - y1;
        const len = Math.hypot(ddx, ddy);
        const nx = (-ddy / len) * 6;
        const ny = (ddx / len) * 6;
        d += `M${(x1 + nx + ddx * 0.12).toFixed(1)} ${(y1 + ny + ddy * 0.12).toFixed(1)}L${(x2 + nx - ddx * 0.12).toFixed(1)} ${(y2 + ny - ddy * 0.12).toFixed(1)}`;
      }
      return `<path d="${d}" stroke="${INK}" stroke-width="1.6" stroke-linecap="round" data-d="${tl(t, 0.45)}"/>`;
    };
    const atom = (/** @type {string} */ k, /** @type {string} */ text, /** @type {number} */ t) =>
      `<g data-f="${tl(t, 0.3)}"><circle cx="${P[k][0]}" cy="${P[k][1]}" r="12" fill="${BG}" stroke="none"/><text x="${P[k][0]}" y="${P[k][1] + 6}" text-anchor="middle" font-size="17" fill="${INK}" stroke="none">${text}</text></g>`;
    const molecule = `${b('C6', 'C5', 0.2)}${b('C5', 'C4', 0.35, true)}${b('C4', 'N3', 0.5)}${b('N3', 'C2', 0.65)}${b('C2', 'N1', 0.8)}${b('N1', 'C6', 0.95)}
      ${b('C5', 'N7', 1.1)}${b('N7', 'C8', 1.25)}${b('C8', 'N9', 1.4, true)}${b('N9', 'C4', 1.55)}
      ${b('C6', 'O6', 1.7, true)}${b('C2', 'O2', 1.8, true)}${b('N1', 'M1', 1.9)}${b('N3', 'M3', 2)}${b('N7', 'M7', 2.1)}
      ${atom('N1', 'N', 1)}${atom('N3', 'N', 0.7)}${atom('N7', 'N', 1.3)}${atom('N9', 'N', 1.5)}${atom('O6', 'O', 1.9)}${atom('O2', 'O', 2)}
      <text x="${P.M1[0] - 4}" y="${P.M1[1] - 2}" text-anchor="end" font-size="14" fill="${MUTED}" stroke="none" data-f="2.1 0.4">CH₃</text>
      <text x="${P.M3[0]}" y="${P.M3[1] + 20}" text-anchor="middle" font-size="14" fill="${MUTED}" stroke="none" data-f="2.2 0.4">CH₃</text>
      <text x="${P.M7[0] + 6}" y="${P.M7[1] - 4}" font-size="14" fill="${MUTED}" stroke="none" data-f="2.3 0.4">CH₃</text>`;
    if (m) {
      return `<svg viewBox="0 70 400 520" fill="none" aria-hidden="true">
        ${molecule}
        <path d="M156 420H244" stroke="${INK}" stroke-opacity=".4" stroke-width="1" data-d="${tl(c1 - 0.4, 0.4)}"/>
        ${label(200, 452, 'CAFÉINE NATURELLE · C₈H₁₀N₄O₂', 'middle', tl(c1 - 0.3, 0.4), INK, 13)}
        ${bigNumber(200, 530, 40, 'mg', c1, 72, 'middle')}
        <path d="M150 552H250" stroke="${acc}" stroke-width="3" data-d="${tl(c1 + 0.6, 0.6)}"/>
        ${label(200, 580, 'PAR PIÈCE', 'middle', tl(c1 + 0.8, 0.4), MUTED, 13)}
      </svg>`;
    }
    return `<svg viewBox="0 0 800 440" fill="none" aria-hidden="true">
      ${molecule}
      <path d="M520 230H556" stroke="${INK}" stroke-opacity=".4" stroke-width="1" data-d="${tl(c1 - 0.4, 0.4)}"/>
      ${label(572, 176, 'CAFÉINE NATURELLE', 'start', tl(c1 - 0.3, 0.4), INK)}
      ${bigNumber(572, 246, 40, 'mg', c1, 64)}
      <path d="M572 266H700" stroke="${acc}" stroke-width="3" data-d="${tl(c1 + 0.6, 0.6)}"/>
      ${label(572, 292, 'PAR PIÈCE', 'start', tl(c1 + 0.8, 0.4))}
      <text x="300" y="412" text-anchor="middle" font-size="13" fill="${MUTED}" stroke="none" data-f="2.4 0.5">C₈H₁₀N₄O₂</text>
    </svg>`;
  },

  /* Formule développée de la L-théanine (acide aminé) */
  theanine(s, acc, m) {
    const c1 = s.cap?.[1]?.[0] ?? 2;
    const pts = [[150, 260], [220, 220], [290, 260], [360, 220], [430, 260], [500, 220], [570, 260], [640, 220]];
    const chain = pts
      .slice(1)
      .map((p, i) => `<path d="M${pts[i][0]} ${pts[i][1]}L${p[0]} ${p[1]}" stroke="${INK}" stroke-width="1.6" stroke-linecap="round" data-d="${tl(0.3 + i * 0.22, 0.4)}"/>`)
      .join('');
    const dbl = (/** @type {number} */ x, /** @type {number} */ y, /** @type {number} */ t) =>
      `<path d="M${x - 5} ${y}V${y + 52}M${x + 5} ${y + 4}V${y + 52}" stroke="${INK}" stroke-width="1.6" data-d="${tl(t, 0.4)}"/>`;
    const txt = (/** @type {number} */ x, /** @type {number} */ y, /** @type {string} */ t, /** @type {number} */ at, anchor = 'middle') =>
      `<g data-f="${tl(at, 0.3)}"><circle cx="${anchor === 'end' ? x - 10 : x}" cy="${y - 6}" r="13" fill="${BG}" stroke="none"/><text x="${x}" y="${y}" text-anchor="${anchor}" font-size="17" fill="${INK}" stroke="none">${t}</text></g>`;
    const molecule = `${chain}${dbl(150, 260, 2)}${dbl(430, 260, 2.1)}
      <path d="M150 260L96 228" stroke="${INK}" stroke-width="1.6" data-d="2.2 0.3"/>
      <path d="M220 220L212 160H228Z" fill="${INK}" stroke="none" data-f="2.3 0.4"/>
      ${txt(150, 330, 'O', 2.2)}${txt(430, 330, 'O', 2.3)}${txt(86, 226, 'HO', 2.4, 'end')}${txt(220, 150, 'NH₂', 2.5)}${txt(500, 226, 'N', 1.4)}
      <text x="500" y="196" text-anchor="middle" font-size="15" fill="${INK}" stroke="none" data-f="1.5 0.3">H</text>`;
    if (m) {
      return `<svg viewBox="0 0 400 520" fill="none" aria-hidden="true">
        <g transform="translate(-54 0) scale(0.7)">${molecule}</g>
        <path d="M150 300H250" stroke="${acc}" stroke-width="3" data-d="${tl(c1 + 0.5, 0.6)}"/>
        ${label(200, 340, 'L-THÉANINE', 'middle', tl(c1 + 0.2, 0.4), INK, 15)}
        ${label(200, 366, 'UN ACIDE AMINÉ · C₇H₁₄N₂O₃', 'middle', tl(c1 + 0.4, 0.4), MUTED, 13)}
      </svg>`;
    }
    return `<svg viewBox="0 0 800 440" fill="none" aria-hidden="true">
      ${molecule}
      <path d="M560 320H690" stroke="${acc}" stroke-width="3" data-d="${tl(c1 + 0.5, 0.6)}"/>
      ${label(690, 350, 'L-THÉANINE', 'end', tl(c1 + 0.2, 0.4), INK)}
      ${label(690, 372, 'UN ACIDE AMINÉ', 'end', tl(c1 + 0.4, 0.4))}
      <text x="380" y="412" text-anchor="middle" font-size="13" fill="${MUTED}" stroke="none" data-f="2.8 0.5">C₇H₁₄N₂O₃</text>
    </svg>`;
  },

  /* Tableau « pour 1 pièce » avec % VNR */
  table(s, acc, m) {
    const rows = (s.items || []).map((/** @type {string} */ line) => line.split('|').map((v) => v.trim()));
    const at = [0.4, 1.7, 2.6, 3.3];
    const G = m ? { l: 10, r: 390, name: 10, val: 236, bar0: 254, barW: 82, pct: 390, fs: 16, y0: 120, step: 62, vw: 400, vh: 470 } : { l: 120, r: 720, name: 120, val: 470, bar0: 510, barW: 150, pct: 720, fs: 19, y0: 168, step: 58, vw: 800, vh: 440 };
    const lab = m ? 13 : 11.5;
    const body = rows
      .map((/** @type {string[]} */ r, /** @type {number} */ i) => {
        const yy = G.y0 + i * G.step;
        const t = at[i] ?? 0.4 + i;
        const num = parseFloat((r[1] || '').replace(',', '.'));
        const dec = (r[1] || '').includes(',') ? 1 : 0;
        const pct = parseFloat(r[3] || '');
        return `<text x="${G.name}" y="${yy}" font-size="${G.fs}" fill="${INK}" stroke="none" data-f="${tl(t, 0.4)}">${esc(r[0] || '')}</text>
          <text x="${G.val}" y="${yy}" text-anchor="end" font-size="${G.fs}" fill="${INK}" stroke="none" data-f="${tl(t, 0.3)}"><tspan data-c="${tl(t, 1)} 0 ${num || 0} ${dec}">0</tspan><tspan dx="4" font-size="${G.fs * 0.74}" fill="${MUTED}">${esc(r[2] || '')}</tspan></text>
          ${Number.isFinite(pct)
            ? `<rect x="${G.bar0}" y="${yy - 12}" width="${G.barW}" height="10" rx="5" fill="${SOFT}" stroke="none" data-f="${tl(t, 0.3)}"/>
               <path d="M${G.bar0 + 5} ${yy - 7}H${(G.bar0 + 5 + ((G.barW - 10) * Math.min(pct, 100)) / 100).toFixed(1)}" stroke="${acc}" stroke-width="10" stroke-linecap="round" data-d="${tl(t + 0.2, 1)}"/>
               <text x="${G.pct}" y="${yy}" text-anchor="end" font-size="${G.fs * 0.8}" fill="${INK}" stroke="none" data-f="${tl(t + 0.3, 0.3)}"><tspan data-c="${tl(t + 0.2, 1)} 0 ${pct} 0">0</tspan> %</text>`
            : `<text x="${G.pct}" y="${yy}" text-anchor="end" font-size="${G.fs * 0.8}" fill="${MUTED}" stroke="none" data-f="${tl(t, 0.3)}">—</text>`}
          <path d="M${G.l} ${yy + 22}H${G.r}" stroke="${INK}" stroke-opacity=".18" stroke-width="1" data-d="${tl(t + 0.1, 0.6)}"/>`;
      })
      .join('');
    const c1 = s.cap?.[1]?.[0] ?? 4.4;
    const foot = G.y0 + rows.length * G.step + 26;
    return `<svg viewBox="0 0 ${G.vw} ${G.vh}" fill="none" aria-hidden="true">
      ${label(G.l, G.y0 - 56, 'POUR 1 PIÈCE', 'start', '0.1 0.4', INK, lab)}
      ${label(G.r, G.y0 - 56, '% VNR*', 'end', '0.1 0.4', INK, lab)}
      <path d="M${G.l} ${G.y0 - 40}H${G.r}" stroke="${INK}" stroke-width="2.5" data-d="0 0.6"/>
      ${body}
      ${label(G.l, foot, 'SANS SUCRE · MENTHE VIVE', 'start', tl(c1, 0.5), INK, lab)}
      <text x="${m ? G.l : G.r}" y="${m ? foot + 24 : foot}" text-anchor="${m ? 'start' : 'end'}" font-size="${m ? 12 : 11}" fill="${MUTED}" stroke="none" data-f="${tl(c1 + 0.3, 0.5)}">*Valeurs nutritionnelles de référence</text>
    </svg>`;
  },

  /* Dose journalière : deux pièces, deux moitiés d'une jauge de 80 mg */
  dose(s, acc, m) {
    const c1 = s.cap?.[1]?.[0] ?? 1.8;
    const G = m ? { g1: 120, g2: 280, gy: 150, gw: 130, l: 30, r: 370, by: 300, vw: 400, vh: 470, fs: 13 } : { g1: 290, g2: 510, gy: 150, gw: 150, l: 160, r: 640, by: 300, vw: 800, vh: 440, fs: 11.5 };
    const mid = (G.l + G.r) / 2;
    return `<svg viewBox="0 0 ${G.vw} ${G.vh}" fill="none" aria-hidden="true">
      ${gum(G.g1, G.gy, G.gw, 0.2)}${gum(G.g2, G.gy, G.gw, 0.7)}
      <g data-f="1.3 0.4"><circle cx="${G.g1}" cy="${G.gy + G.gw * 0.31 + 26}" r="13" stroke="${INK}" stroke-width="1.2"/><text x="${G.g1}" y="${G.gy + G.gw * 0.31 + 31}" text-anchor="middle" font-size="14" fill="${INK}" stroke="none">1</text></g>
      <g data-f="1.5 0.4"><circle cx="${G.g2}" cy="${G.gy + G.gw * 0.31 + 26}" r="13" stroke="${INK}" stroke-width="1.2"/><text x="${G.g2}" y="${G.gy + G.gw * 0.31 + 31}" text-anchor="middle" font-size="14" fill="${INK}" stroke="none">2</text></g>
      ${label(G.l, G.by - 18, 'PAR JOUR, AU MAXIMUM', 'start', tl(c1 - 0.5, 0.4), INK, G.fs)}
      <rect x="${G.l}" y="${G.by - 8}" width="${G.r - G.l}" height="16" rx="8" fill="${SOFT}" stroke="none" data-f="${tl(c1 - 0.6, 0.4)}"/>
      <path d="M${G.l + 8} ${G.by}H${mid - 8}" stroke="${acc}" stroke-width="16" stroke-linecap="round" data-d="${tl(c1, 0.8)}"/>
      <path d="M${mid + 8} ${G.by}H${G.r - 8}" stroke="${acc}" stroke-width="16" stroke-linecap="round" data-d="${tl(c1 + 0.8, 0.8)}"/>
      <path d="M${mid} ${G.by - 18}V${G.by + 18}" stroke="${INK}" stroke-opacity=".5" stroke-width="1" data-f="${tl(c1 - 0.4, 0.4)}"/>
      ${label((G.l + mid) / 2, G.by + 40, '40 MG', 'middle', tl(c1 + 0.2, 0.4), MUTED, G.fs)}
      ${label((mid + G.r) / 2, G.by + 40, '40 MG', 'middle', tl(c1 + 1, 0.4), MUTED, G.fs)}
      ${m ? bigNumber(200, 410, 80, 'mg', c1, 56, 'middle') : bigNumber(660, G.by + 10, 80, 'mg', c1, 32)}
    </svg>`;
  },
};

/** Prépare les tracés d'un schéma (longueurs) ; à appeler une fois inséré dans le DOM */
function prepareArt(/** @type {Element} */ root) {
  root.querySelectorAll('[data-d]').forEach((el) => {
    const path = /** @type {SVGGeometryElement} */ (el);
    const len = typeof path.getTotalLength === 'function' ? path.getTotalLength() : 0;
    /** @type {SVGElement} */ (el).dataset.len = String(len);
    /** @type {SVGElement} */ (el).style.strokeDasharray = `${len} ${len}`;
  });
}

/** Met le schéma dans l'état correspondant au temps t (secondes depuis le début du plan) */
function animateArt(/** @type {Element} */ root, /** @type {number} */ t) {
  const span = (/** @type {string} */ v) => {
    const [a, b] = v.split(' ').map(Number);
    return ease((t - a) / (b || 0.0001));
  };
  root.querySelectorAll('[data-d]').forEach((el) => {
    const e = /** @type {SVGElement} */ (el);
    e.style.strokeDashoffset = String(Number(e.dataset.len || 0) * (1 - span(e.dataset.d || '0 1')));
  });
  root.querySelectorAll('[data-f]').forEach((el) => {
    const e = /** @type {SVGElement} */ (el);
    e.style.opacity = String(span(e.dataset.f || '0 1'));
  });
  root.querySelectorAll('[data-c]').forEach((el) => {
    const e = /** @type {SVGElement} */ (el);
    const [a, b, from, to, dec] = (e.dataset.c || '').split(' ').map(Number);
    e.textContent = fr(from + (to - from) * ease((t - a) / (b || 0.0001)), dec || 0);
  });
  root.querySelectorAll('[data-m]').forEach((el) => {
    const e = /** @type {SVGElement} */ (el);
    const [id, a, b] = (e.dataset.m || '').split(' ');
    const path = /** @type {SVGGeometryElement | null} */ (root.querySelector(`#${id}`));
    if (!path) return;
    const p = ease((t - Number(a)) / (Number(b) || 0.0001));
    const pt = path.getPointAtLength(path.getTotalLength() * p);
    e.setAttribute('transform', `translate(${pt.x.toFixed(1)} ${pt.y.toFixed(1)})`);
    e.style.opacity = t < Number(a) - 0.05 ? '0' : '1';
  });
  root.querySelectorAll('[data-g]').forEach((el) => {
    const e = /** @type {SVGElement} */ (el);
    const [a, b, rx0, rx1, ry0, ry1] = (e.dataset.g || '').split(' ').map(Number);
    const raw = (t - a) / (b || 0.0001);
    const p = ease(raw);
    e.setAttribute('rx', String(rx0 + (rx1 - rx0) * p));
    e.setAttribute('ry', String(ry0 + (ry1 - ry0) * p));
    e.style.opacity = raw <= 0 || raw >= 1 ? '0' : String(0.9 * (1 - p));
  });
}

class CgParcours extends HTMLElement {
  #controller = new AbortController();
  /** @type {Record<string, any>} */
  #data = {};
  #g = '';
  #scene = 0;
  #t = 0;
  #last = 0;
  #frame = 0;
  #started = false;
  #paused = true;
  #ended = false;
  #sound = true;
  #capKey = '';
  /** @type {HTMLElement[]} */
  #layers = [];
  /** @type {HTMLElement[]} */
  #overlays = [];
  /** @type {HTMLAudioElement[]} */
  #audios = [];

  connectedCallback() {
    const { signal } = this.#controller;
    try {
      this.#data = JSON.parse(this.querySelector('[data-cg-parcours-data]')?.textContent || '{}');
    } catch (error) {
      this.#data = {};
    }
    document.addEventListener(
      'click',
      (event) => {
        const target = event.target instanceof Element ? event.target.closest('[data-cg-parcours-open]') : null;
        if (!(target instanceof HTMLElement)) return;
        const gamme = target.dataset.cgParcoursOpen || '';
        if (!this.#data[gamme]) return;
        event.preventDefault();
        event.stopPropagation();
        this.open(gamme);
      },
      { signal, capture: true }
    );

    this.#dialog?.addEventListener('close', () => this.#stop(), { signal });
    this.#dialog?.addEventListener(
      'click',
      (event) => {
        if (event.target === this.#dialog) this.#dialog?.close();
      },
      { signal }
    );
    this.querySelector('[data-pc-close]')?.addEventListener('click', () => this.#dialog?.close(), { signal });
    this.querySelector('[data-pc-fs]')?.addEventListener(
      'click',
      () => {
        const stage = this.#stage;
        if (document.fullscreenElement) document.exitFullscreen().catch(() => {});
        else stage?.requestFullscreen?.().catch(() => {});
      },
      { signal }
    );
    this.querySelector('[data-pc-sound]')?.addEventListener(
      'click',
      (event) => {
        this.#sound = !this.#sound;
        this.#audios.forEach((audio) => (audio.muted = !this.#sound));
        const button = /** @type {HTMLElement} */ (event.currentTarget);
        button.setAttribute('aria-pressed', String(this.#sound));
        button.setAttribute('aria-label', this.#sound ? 'Couper le son' : 'Activer le son');
      },
      { signal }
    );
    this.#chapters?.addEventListener(
      'click',
      (event) => {
        const button = event.target instanceof Element ? event.target.closest('button') : null;
        if (!(button instanceof HTMLButtonElement)) return;
        if (button.classList.contains('cg-pc__pill')) {
          if (!this.#started || this.#ended) this.#go(this.#ended ? 0 : this.#scene);
          else this.#setPaused(!this.#paused);
          return;
        }
        const chapter = Number(button.dataset.ch);
        this.#go(this.#scenes.findIndex((scene) => scene.ch === chapter));
      },
      { signal }
    );
    this.querySelector('[data-pc-layers]')?.addEventListener(
      'click',
      () => {
        if (!this.#started) this.#go(0);
        else if (!this.#ended) this.#setPaused(!this.#paused);
      },
      { signal }
    );
  }

  disconnectedCallback() {
    this.#controller.abort();
    this.#stop();
  }

  get #dialog() {
    return /** @type {HTMLDialogElement | null} */ (this.querySelector('dialog'));
  }

  get #stage() {
    return /** @type {HTMLElement | null} */ (this.querySelector('[data-pc-stage]'));
  }

  get #chapters() {
    return /** @type {HTMLElement | null} */ (this.querySelector('[data-pc-chapters]'));
  }

  get #caption() {
    return /** @type {HTMLElement | null} */ (this.querySelector('[data-pc-caption]'));
  }

  /** @returns {any[]} */
  get #scenes() {
    return this.#data[this.#g]?.scenes ?? [];
  }

  /** @param {string} gamme */
  open(gamme) {
    this.#g = gamme;
    this.#build();
    this.#dialog?.showModal();
    this.#go(0);
    cancelAnimationFrame(this.#frame);
    this.#last = 0;
    this.#frame = requestAnimationFrame(this.#tick);
  }

  #stop() {
    cancelAnimationFrame(this.#frame);
    this.#audios.forEach((audio) => audio.pause());
    this.#layers.forEach((layer) => layer.querySelector('video')?.pause());
    this.#started = false;
    this.#paused = true;
  }

  #build() {
    const stage = this.#stage;
    const data = this.#data[this.#g];
    if (!stage || !data) return;
    stage.dataset.g = this.#g;
    const layersEl = /** @type {HTMLElement} */ (this.querySelector('[data-pc-layers]'));
    const overlaysEl = /** @type {HTMLElement} */ (this.querySelector('[data-pc-overlays]'));
    layersEl.textContent = '';
    overlaysEl.textContent = '';
    this.#audios.forEach((audio) => audio.pause());
    this.#layers = [];
    this.#overlays = [];
    this.#audios = [];

    const acc = this.#g === 'energisant' ? '#2f80ff' : '#48d84e';
    const mobile = window.matchMedia('(max-width: 749px)').matches;
    data.scenes.forEach((/** @type {any} */ s) => {
      s.dur = Math.max(4.6, (Number(s.audioDur) || 4) + 1.1);
      const layer = document.createElement('div');
      layer.className = 'cg-pc__layer' + (s.soft || s.cardTitle ? ' is-soft' : '') + (s.kind !== 'media' && s.kind !== 'end' ? ' is-light' : '');
      layer.style.setProperty('--mpos', s.mpos || '60% 50%');
      layer.style.setProperty('--dur', s.dur + 's');
      if (s.video) {
        const video = document.createElement('video');
        video.muted = true;
        video.playsInline = true;
        video.preload = 'auto';
        if (s.img) video.poster = s.img;
        video.src = s.video;
        video.setAttribute('muted', '');
        video.setAttribute('playsinline', '');
        layer.append(video);
      } else if (s.img) {
        const img = document.createElement('img');
        img.src = s.img;
        img.alt = '';
        layer.append(img);
      }
      layersEl.append(layer);
      this.#layers.push(layer);

      const overlay = document.createElement('div');
      overlay.className = 'cg-pc__ov';
      let html = '';
      if (s.cardTitle) {
        html += `<div class="cg-pc__card">${s.cardImg ? `<div class="cg-pc__card-media"><img src="${esc(s.cardImg)}" alt=""></div>` : ''}<b>${esc(s.cardTitle)}</b><span>${esc(s.cardText || '')}</span></div>`;
      }
      if (s.tags?.length) {
        html += `<div class="cg-pc__tags">${s.tags.map((/** @type {string} */ tag, /** @type {number} */ i) => `<span style="--d:${0.4 + i * 0.5}s">${esc(tag)}</span>`).join('')}</div>`;
      }
      if (ART[s.kind]) html += `<div class="cg-pc__art">${ART[s.kind](s, acc, mobile)}</div>`;
      if (s.heading) html = `<p class="cg-pc__heading">${s.headingNum ? `<span>${esc(s.headingNum)}</span>` : ''}${esc(s.heading)}</p>${html}`;
      if (s.legal) html += `<p class="cg-pc__legal">${esc(s.legal)}</p>`;
      if (s.kind === 'end') {
        html += `<div class="cg-pc__end"><span class="cg-pc__name">${esc(data.product)}</span><span class="cg-pc__row">${data.url ? `<a class="cg-pc__cta" href="${esc(data.url)}">${esc(data.cta)}</a>` : ''}<button type="button" class="cg-pc__ghost" data-pc-replay>${esc(data.replay)}</button></span></div>`;
        overlay.style.pointerEvents = 'auto';
      }
      overlay.innerHTML = html;
      overlaysEl.append(overlay);
      prepareArt(overlay);
      animateArt(overlay, 0);
      this.#overlays.push(overlay);

      const audio = new Audio();
      audio.preload = 'auto';
      if (s.audio) audio.src = s.audio;
      audio.muted = !this.#sound;
      this.#audios.push(audio);
    });
    overlaysEl.querySelectorAll('[data-pc-replay]').forEach((button) => button.addEventListener('click', () => this.#go(0)));

    const chapters = this.#chapters;
    if (chapters) {
      chapters.textContent = '';
      data.chapters.forEach((/** @type {string} */ _name, /** @type {number} */ i) => {
        if (i > 0) {
          const sep = document.createElement('span');
          sep.className = 'cg-pc__sep';
          chapters.append(sep);
        }
        const button = document.createElement('button');
        button.type = 'button';
        button.dataset.ch = String(i);
        chapters.append(button);
      });
    }
  }

  /** @param {number} ch */
  #chapterSpan(ch) {
    let start = 0;
    let len = 0;
    this.#scenes.forEach((s) => {
      if (s.ch < ch) start += s.dur;
      if (s.ch === ch) len += s.dur;
    });
    return { start, len };
  }

  #elapsed() {
    return this.#scenes.slice(0, this.#scene).reduce((sum, s) => sum + s.dur, 0) + this.#t;
  }

  #renderChapters() {
    const data = this.#data[this.#g];
    const current = this.#scenes[this.#scene]?.ch ?? 0;
    this.#chapters?.querySelectorAll('button').forEach((button) => {
      const i = Number(button.dataset.ch);
      if (i === current) {
        button.className = 'cg-pc__pill';
        button.setAttribute('aria-label', (this.#paused ? 'Lire : ' : 'Pause : ') + data.chapters[i]);
        button.innerHTML = `<span class="cg-pc__fill"></span>${this.#paused ? ICON_PLAY : ICON_PAUSE}<span class="cg-pc__pill-name">${esc(data.chapters[i])}</span>`;
      } else {
        button.className = 'cg-pc__dot';
        button.setAttribute('aria-label', `Chapitre ${i + 1} : ${data.chapters[i]}`);
        button.textContent = '';
      }
    });
    const chip = this.querySelector('[data-pc-chip]');
    if (chip) chip.textContent = `Chapitre ${current + 1}`;
  }

  /** @param {any} s */
  #renderCaption(s) {
    const caption = this.#caption;
    if (!caption || !s.cap?.length) return;
    let index = -1;
    s.cap.forEach((/** @type {[number, string]} */ c, /** @type {number} */ k) => {
      if (this.#t >= c[0]) index = k;
    });
    if (index < 0) return;
    const chunk = s.cap[index];
    const next = s.cap[index + 1] ? s.cap[index + 1][0] : Number(s.audioDur) || chunk[0] + 3;
    const key = `${this.#scene}:${index}`;
    if (key !== this.#capKey) {
      this.#capKey = key;
      caption.innerHTML = chunk[1]
        .split(' ')
        .map((/** @type {string} */ word) => `<span>${esc(word)}</span>`)
        .join(' ');
    }
    const spans = caption.querySelectorAll('span');
    const span = Math.max(0.6, (next - chunk[0]) * 0.92);
    spans.forEach((word, i) => {
      if (reduceMotion.matches || this.#t >= chunk[0] + (i / spans.length) * span) word.classList.add('is-in');
    });
  }

  /** @param {number} i */
  #show(i) {
    const s = this.#scenes[i];
    if (!s) return;
    this.#layers.forEach((layer, k) => {
      const on = k === i;
      layer.classList.toggle('is-on', on);
      const video = layer.querySelector('video');
      if (video) {
        if (on) {
          try {
            video.currentTime = 0;
          } catch (error) {
            // vidéo pas encore prête
          }
          video.playbackRate = Math.min(1, 6 / s.dur);
          if (!this.#paused) video.play().catch(() => {});
        } else {
          video.pause();
        }
      }
      layer.classList.remove('is-kb');
      if (on && this.#scenes[k].kb && !reduceMotion.matches) {
        void layer.offsetWidth;
        layer.classList.add('is-kb');
      }
    });
    this.#overlays.forEach((overlay, k) => overlay.classList.toggle('is-on', k === i));
    this.#audios.forEach((audio, k) => {
      if (k !== i) audio.pause();
    });
    const audio = this.#audios[i];
    if (audio) {
      try {
        audio.currentTime = 0;
      } catch (error) {
        // audio pas encore prêt
      }
      audio.muted = !this.#sound;
      if (!this.#paused) audio.play().catch(() => {});
    }
    this.#capKey = '';
    if (this.#caption) this.#caption.textContent = '';
    this.#renderChapters();
  }

  /** @param {number} i */
  #go(i) {
    this.#scene = Math.max(0, Math.min(i, this.#scenes.length - 1));
    this.#t = 0;
    this.#ended = false;
    this.#started = true;
    this.#paused = false;
    this.#show(this.#scene);
  }

  /** @param {boolean} paused */
  #setPaused(paused) {
    this.#paused = paused;
    const video = this.#layers[this.#scene]?.querySelector('video');
    const audio = this.#audios[this.#scene];
    const s = this.#scenes[this.#scene];
    if (paused) {
      video?.pause();
      audio?.pause();
    } else {
      video?.play().catch(() => {});
      if (audio && audio.currentTime < (Number(s?.audioDur) || 0)) audio.play().catch(() => {});
    }
    this.#stage?.querySelectorAll('.cg-pc__layer.is-kb img').forEach((img) => {
      /** @type {HTMLElement} */ (img).style.animationPlayState = paused ? 'paused' : 'running';
    });
    this.#renderChapters();
  }

  /** @param {number} now */
  #tick = (now) => {
    const dt = this.#last ? Math.min(0.1, (now - this.#last) / 1000) : 0;
    this.#last = now;
    const scenes = this.#scenes;
    const s = scenes[this.#scene];
    if (s && this.#started && !this.#paused && !this.#ended) {
      this.#t += dt;
      if (this.#t >= s.dur) {
        if (this.#scene < scenes.length - 1) {
          this.#go(this.#scene + 1);
        } else {
          this.#t = s.dur;
          this.#ended = true;
          this.#paused = true;
          this.#renderChapters();
        }
      }
    }
    const current = scenes[this.#scene];
    if (current && this.#started) this.#renderCaption(current);
    const overlay = this.#overlays[this.#scene];
    if (overlay && this.#started) animateArt(overlay, reduceMotion.matches ? 999 : this.#t);
    if (current) {
      const { start, len } = this.#chapterSpan(current.ch);
      const pill = /** @type {HTMLElement | null} */ (this.#chapters?.querySelector('.cg-pc__pill'));
      if (pill && len) pill.style.setProperty('--p', Math.min(1, (this.#elapsed() - start) / len).toFixed(4));
    }
    if (this.#dialog?.open) this.#frame = requestAnimationFrame(this.#tick);
  };
}

if (!customElements.get('cg-parcours')) customElements.define('cg-parcours', CgParcours);
